import { ScheduleSpacing } from '../../../scheduling/schedule.rule';

/**
 * How far back the KPI reads completions before the month starts, so a month's first gap has the
 * previous one to measure from. A quarter covers any spacing a monthly-reported contract would set.
 */
export const VISIT_SPACING_LOOKBACK_DAYS = 93;

export enum VisitSpacingBreach {
    /** Closer to the previous completion than `minDays`. */
    TooSoon = 'TooSoon',
    /** Further from the previous completion than `maxDays`. */
    TooLate = 'TooLate',
}

/** One completed task as the KPI reads it off a stored Visit. */
export interface VisitSpacingCompletion {
    serviceLineTaskId: string;
    completedAt: Date;
    spacing?: ScheduleSpacing;
}

export interface VisitSpacingTaskKpi {
    serviceLineTaskId: string;
    /** The rule of the month's last completion; a mid-month rule change is judged per gap. */
    spacing?: ScheduleSpacing;
    completed: number;
    gapsOk: number;
    gapsBreached: number;
}

/** One Service Line's spacing results for one month, computed on read from its stored Visits. */
export interface VisitSpacingKpi {
    /** Visits the line owed this month (not skipped or cancelled); absent when the caller did not count them. */
    required?: number;
    completed: number;
    gapsOk: number;
    gapsBreached: number;
    tasks: VisitSpacingTaskKpi[];
}

/**
 * Whether a Visit on `nextDate` would break the spacing rule against the previous completion.
 * Undefined when there is no previous completion or no rule: a first visit has nothing to be spaced from.
 * Dates are compared as calendar days in `timezone`, so a late-evening completion counts on its local day.
 */
export function spacingBreach(prevCompletedAt: Date | string | null | undefined, nextDate: Date | string, rule: ScheduleSpacing | null | undefined, timezone = 'UTC'): VisitSpacingBreach | undefined {

    if (!prevCompletedAt || !rule) return undefined;
    const gap = spacingGapDays(prevCompletedAt, nextDate, timezone);
    if (gap === undefined) return undefined;
    if (typeof rule.minDays === 'number' && gap < rule.minDays) return VisitSpacingBreach.TooSoon;
    if (typeof rule.maxDays === 'number' && gap > rule.maxDays) return VisitSpacingBreach.TooLate;

    return undefined;
}

/**
 * The spacing facts of one spaced task on a stored Visit, read off its line's other stored Visits.
 * `previous` / `next` are local dates either side of the Visit's current booked day: a completion where the
 * task was done, else the booked day of a live Visit that carries the task.
 */
export interface VisitSpacingFacts {
    taskId: string;
    spacing: ScheduleSpacing;
    previous?: string;
    next?: string;
}

/**
 * Plan section 16 — the warning a reschedule to `localDate` earns, or undefined when it keeps every spaced
 * task's rule. Judged against the neighbours on each side of the NEW date, so a move past a neighbour is
 * measured from that neighbour. A warning, never a block: spacing is reported in the KPI either way.
 */
export function visitSpacingWarning(facts: readonly VisitSpacingFacts[] | null | undefined, localDate: string): string | undefined {

    for (const fact of facts ?? []) {
        if (!hasSpacing(fact.spacing)) continue;
        const neighbours = [fact.previous, fact.next].filter((date): date is string => !!date);
        const before = neighbours.filter(date => date <= localDate).sort().pop();
        const after = neighbours.filter(date => date > localDate).sort()[0];
        const warning = (before ? spacingGapWarning(spacingGapDays(before, localDate), fact.spacing, 'after the previous visit') : undefined)
            ?? (after ? spacingGapWarning(spacingGapDays(localDate, after), fact.spacing, 'before the next visit') : undefined);
        if (warning) return warning;
    }

    return undefined;
}

function spacingGapWarning(gap: number | undefined, rule: ScheduleSpacing, relation: string): string | undefined {

    if (gap === undefined) return undefined;
    if (typeof rule.minDays === 'number' && gap < rule.minDays) {
        return gap === 0
            ? `Same day as the ${relation.replace(/^(after|before) the /, '')} (minimum ${rule.minDays})`
            : `Only ${dayCount(gap)} ${relation} (minimum ${rule.minDays})`;
    }
    if (typeof rule.maxDays === 'number' && gap > rule.maxDays) return `${dayCount(gap)} ${relation} (maximum ${rule.maxDays})`;

    return undefined;
}

function dayCount(days: number): string {

    return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** Whole calendar days from `from` to `to` in `timezone`; undefined for an unreadable date. */
export function spacingGapDays(from: Date | string, to: Date | string, timezone = 'UTC'): number | undefined {

    const start = localDayNumber(from, timezone);
    const end = localDayNumber(to, timezone);

    return start === undefined || end === undefined ? undefined : end - start;
}

/**
 * One line's spacing KPI for `month` (YYYY-MM in `timezone`). Completions may start before the month:
 * those only supply the previous completion for the month's first gap. Each gap belongs to the month of
 * its later completion and is judged against that completion's rule. Tasks with no rule are not counted.
 */
export function spacingKpiOf(completions: readonly VisitSpacingCompletion[], month: string, timezone: string, required?: number): VisitSpacingKpi {

    const byTask = new Map<string, VisitSpacingCompletion[]>();
    for (const completion of completions) {
        if (!hasSpacing(completion.spacing)) continue;
        byTask.set(completion.serviceLineTaskId, [...byTask.get(completion.serviceLineTaskId) ?? [], completion]);
    }

    const tasks = [...byTask]
        .map(([serviceLineTaskId, entries]) => taskKpi(serviceLineTaskId, entries, month, timezone))
        .filter(task => task.completed > 0)
        .sort((left, right) => left.serviceLineTaskId.localeCompare(right.serviceLineTaskId));

    return {
        ...(required !== undefined ? { required } : {}),
        completed: sum(tasks, task => task.completed),
        gapsOk: sum(tasks, task => task.gapsOk),
        gapsBreached: sum(tasks, task => task.gapsBreached),
        tasks,
    };
}

function taskKpi(serviceLineTaskId: string, entries: VisitSpacingCompletion[], month: string, timezone: string): VisitSpacingTaskKpi {

    const ordered = [...entries].sort((left, right) => new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime());
    const kpi: VisitSpacingTaskKpi = { serviceLineTaskId, completed: 0, gapsOk: 0, gapsBreached: 0 };
    ordered.forEach((entry, index) => {
        if (localDate(entry.completedAt, timezone)?.slice(0, 7) !== month) return;
        kpi.completed++;
        kpi.spacing = entry.spacing;
        const previous = ordered[index - 1];
        if (!previous) return;
        if (spacingBreach(previous.completedAt, entry.completedAt, entry.spacing, timezone)) kpi.gapsBreached++;
        else kpi.gapsOk++;
    });

    return kpi;
}

function hasSpacing(spacing: ScheduleSpacing | undefined): spacing is ScheduleSpacing {

    return typeof spacing?.minDays === 'number' || typeof spacing?.maxDays === 'number';
}

function localDayNumber(value: Date | string, timezone: string): number | undefined {

    const date = localDate(value, timezone);

    return date ? Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))) / 86_400_000 : undefined;
}

/** A bare YYYY-MM-DD is already a local date; an instant is read in `timezone`. en-CA gives ISO ordering. */
function localDate(value: Date | string, timezone: string): string | undefined {

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const instant = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(instant.getTime())) return undefined;
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
    } catch {
        return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
    }
}

function sum<T>(values: readonly T[], of: (value: T) => number): number {

    return values.reduce((total, value) => total + of(value), 0);
}
